import React, { useState } from 'react';
import parse from 'html-react-parser';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    LuCheck, LuChevronDown, LuCircleAlert, LuCircleCheck, LuHourglass, LuLoaderCircle, LuPackage, LuShieldCheck,
} from 'react-icons/lu';

import PATHS from '../../../Routers/PATHS';
import { useCheckout } from '../hooks/useCheckout';
import { formatUSD, packageIdOf } from '../pricing';
import '../styles/prose.css';
import { Button, Modal } from '../ui';

/**
 * The checkout behind "اشترك الآن" and "تجديد الاشتراك" — on the certificate
 * page's comparison table and on the subscriptions page.
 *
 * DRAWN BY EXTENSION
 *
 * The design file has no frame for it. This replaces the app's old checkout
 * form, which both pages used to mount inside their own dialogue and which
 * brought semantic-ui's look with it. It is built from what the design does
 * draw: the dialogue the subscription card opens, that card's icon tiles and
 * type sizes, and the brand's flat fill on the one button that matters.
 *
 * Read top to bottom it is a receipt — what is being bought, a code if the
 * reader has one, what it comes to, the policy to accept — with the button
 * pinned underneath so it never scrolls out of reach behind a long policy.
 * A line that would say nothing is not drawn: a package with no discount has
 * no discount line, and a free one has no price at all, only "مجاناً".
 *
 * Written in the order the Arabic reader meets it: the tile before the name,
 * the field before its button, each label before its figure. Figures carry
 * their own `dir`, since "$400" is Latin inside an Arabic line.
 *
 * What it does is `useCheckout`'s, and unchanged from the old form. `onDone`
 * runs only for a package that costs nothing; a priced one leaves the page for
 * the payment host. `onSubscriptions` is where "الاشتراكات" goes after a
 * refusal — by default the subscriptions page, which a caller already on that
 * page replaces with a reload.
 */

const CODE_FIELD_ID = 'v2-checkout-code';
const POLICY_ID = 'v2-checkout-policy';

const FOCUS_RING = 'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-v2-brand';

const Money = ({ value, minus = false }) => (
    <span dir="ltr">{minus ? '−' : ''}{formatUSD(value)}</span>
);

const Line = ({ label, saving = false, children }) => (
    <div className="tw-flex tw-items-center tw-justify-between tw-gap-[16px]">
        <span className="tw-text-[14px] tw-leading-[1.6] tw-text-v2-text-excerpt">{label}</span>
        <span className={`tw-text-[14px] tw-font-bold tw-leading-[1.6] ${saving ? 'tw-text-v2-student-done' : 'tw-text-v2-neutral-900'}`}>
            {children}
        </span>
    </div>
);

/**
 * The policy's checkbox. The native input is kept, invisible, over the drawn
 * box — it is what the keyboard, a screen reader and the wrapping <label> all
 * talk to. The student wizard has one like it; that one is the wizard's, with
 * an indeterminate state this has no use for.
 */
const Tick = ({ checked, disabled, onChange }) => (
    <span className="tw-relative tw-inline-flex tw-size-[20px] tw-shrink-0">
        <input
            type="checkbox"
            checked={checked}
            disabled={disabled}
            onChange={onChange}
            className="tw-peer tw-absolute tw-inset-0 tw-m-0 tw-size-full tw-cursor-pointer tw-appearance-none tw-opacity-0 disabled:tw-cursor-not-allowed"
        />
        <span
            aria-hidden
            className={
                'tw-pointer-events-none tw-flex tw-size-full tw-items-center tw-justify-center tw-rounded-[5px] ' +
                'tw-border-[1.6px] tw-border-solid tw-transition-colors ' +
                'peer-focus-visible:tw-ring-2 peer-focus-visible:tw-ring-v2-brand peer-focus-visible:tw-ring-offset-1 ' +
                (checked ? 'tw-border-v2-brand tw-bg-v2-brand tw-text-white' : 'tw-border-v2-student-box tw-bg-white')
            }
        >
            {checked && <LuCheck size={14} strokeWidth={3} />}
        </span>
    </span>
);

const Checkout = ({ pkg, onClose, onDone, onSubscriptions }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const [policyOpen, setPolicyOpen] = useState(false);

    const {
        name, days, totals, code, setCode, applyCode, promotion,
        needsPolicy, policy, agreed, setAgreed,
        canSubmit, submitting, submit, error, clearError,
    } = useCheckout(pkg, onDone);

    const title = t('v2.checkout.title');

    const goToSubscriptions = () => {
        if (onSubscriptions) {
            onSubscriptions();
            return;
        }

        onClose();
        history.push(PATHS.USER_SUBSCRIPTIONS.URL);
    };

    if (error) {
        return (
            <Modal
                isOpen
                size="narrow"
                title={title}
                onClose={onClose}
                footer={(
                    <div className="tw-flex tw-gap-[12px]">
                        <Button className="tw-flex-1" onClick={goToSubscriptions}>
                            {t('v2.checkout.subscriptions')}
                        </Button>
                        <Button variant="outline" className="tw-flex-1" onClick={clearError}>
                            {t('v2.checkout.back')}
                        </Button>
                    </div>
                )}
            >
                <div role="alert" className="tw-flex tw-flex-col tw-items-center tw-gap-[12px] tw-py-[12px] tw-text-center">
                    <span
                        aria-hidden
                        className="tw-flex tw-size-[48px] tw-items-center tw-justify-center tw-rounded-full tw-bg-v2-no-tint tw-text-v2-no"
                    >
                        <LuCircleAlert size={22} />
                    </span>
                    <p className="tw-m-0 tw-text-[15px] tw-leading-[1.8] tw-text-v2-neutral-900">{error}</p>
                </div>
            </Modal>
        );
    }

    let policyBody;
    if (policy.loading) policyBody = <p className="tw-m-0">{t('v2.checkout.policyLoading')}</p>;
    else if (policy.error) policyBody = <p role="alert" className="tw-m-0">{policy.error}</p>;
    else if (policy.html) policyBody = parse(policy.html);
    else policyBody = <p className="tw-m-0">{t('v2.checkout.policyEmpty')}</p>;

    const isPriced = totals.price > 0;

    return (
        <Modal
            isOpen
            size="narrow"
            title={title}
            onClose={onClose}
            footer={(
                <div className="tw-flex tw-flex-col tw-gap-[10px]">
                    <Button className="tw-w-full" disabled={!canSubmit} aria-busy={submitting} onClick={submit}>
                        {submitting && <LuLoaderCircle size={16} aria-hidden className="tw-animate-spin" />}
                        {totals.total > 0 ? t('v2.checkout.pay') : t('v2.checkout.confirm')}
                    </Button>

                    {totals.total > 0 && (
                        <p className="tw-m-0 tw-flex tw-items-center tw-justify-center tw-gap-[6px] tw-text-center tw-text-[12px] tw-leading-[1.5] tw-text-v2-text-excerpt">
                            <LuShieldCheck size={14} aria-hidden className="tw-shrink-0 tw-text-v2-student-done" />
                            {t('v2.checkout.secure')}
                        </p>
                    )}
                </div>
            )}
        >
            <div className="tw-flex tw-flex-col tw-gap-[20px]">
                <div className="tw-flex tw-items-center tw-gap-[12px] tw-rounded-[12px] tw-bg-v2-brand-tint tw-p-[16px]">
                    <span
                        aria-hidden
                        className="tw-flex tw-size-[44px] tw-shrink-0 tw-items-center tw-justify-center tw-rounded-[10px] tw-bg-white tw-text-v2-brand"
                    >
                        <LuPackage size={20} />
                    </span>

                    <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-[2px] tw-text-start">
                        <p className="tw-m-0 tw-break-words tw-text-[15px] tw-font-bold tw-leading-[1.6] tw-text-v2-neutral-900">
                            {name}
                        </p>
                        {days > 0 && (
                            <p className="tw-m-0 tw-flex tw-items-center tw-gap-[6px] tw-text-[13px] tw-leading-[1.6] tw-text-v2-text-excerpt">
                                <LuHourglass size={13} aria-hidden className="tw-shrink-0" />
                                {t('v2.checkout.duration', { days })}
                            </p>
                        )}
                    </div>
                </div>

                <div className="tw-flex tw-flex-col tw-gap-[8px]">
                    <label
                        htmlFor={CODE_FIELD_ID}
                        className="tw-m-0 tw-text-start tw-text-[14px] tw-font-bold tw-leading-[1.6] tw-text-v2-neutral-900"
                    >
                        {t('v2.checkout.code')}
                    </label>

                    <div className="tw-flex tw-gap-[8px]">
                        <input
                            id={CODE_FIELD_ID}
                            type="text"
                            autoComplete="off"
                            value={code}
                            disabled={submitting}
                            placeholder={t('v2.checkout.codePlaceholder')}
                            aria-invalid={promotion.note?.tone === 'error'}
                            onChange={(event) => setCode(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key !== 'Enter') return;
                                event.preventDefault();
                                applyCode();
                            }}
                            // The `!` utilities answer the global `input` rule in
                            // public/css/rtl.css, which forces a ground, a radius
                            // and `border: none` on every field. `landing`'s
                            // `Field.jsx` has the whole story.
                            className={
                                'tw-h-[44px] tw-min-w-0 tw-flex-1 !tw-rounded-[10px] !tw-border !tw-border-solid !tw-bg-white ' +
                                'tw-px-[14px] tw-py-0 tw-text-start tw-text-[14px] tw-leading-[1.6] tw-text-v2-neutral-900 ' +
                                'placeholder:tw-text-v2-neutral-400 tw-transition-colors focus:tw-outline-none ' +
                                'focus:tw-ring-2 focus:tw-ring-v2-brand/30 disabled:tw-opacity-60 ' +
                                (promotion.note?.tone === 'error'
                                    ? '!tw-border-v2-error'
                                    : '!tw-border-v2-neutral-200 focus:!tw-border-v2-brand')
                            }
                        />

                        <button
                            type="button"
                            onClick={applyCode}
                            disabled={!code.trim() || promotion.checking || submitting}
                            aria-busy={promotion.checking}
                            className={
                                'tw-inline-flex tw-h-[44px] tw-min-w-[84px] tw-shrink-0 tw-items-center tw-justify-center ' +
                                'tw-rounded-[10px] tw-border tw-border-solid tw-border-v2-brand tw-bg-white tw-px-[18px] ' +
                                'tw-text-[14px] tw-font-bold tw-leading-[1.6] tw-text-v2-brand tw-transition-colors ' +
                                'hover:tw-bg-v2-brand-tint disabled:tw-cursor-not-allowed disabled:tw-opacity-50 ' +
                                'disabled:hover:tw-bg-white ' + FOCUS_RING
                            }
                        >
                            {promotion.checking
                                ? <LuLoaderCircle size={16} aria-hidden className="tw-animate-spin" />
                                : t('v2.checkout.applyCode')}
                        </button>
                    </div>

                    {promotion.note && (
                        <p
                            role="status"
                            className={
                                'tw-m-0 tw-flex tw-items-center tw-gap-[6px] tw-text-start tw-text-[13px] tw-leading-[1.6] ' +
                                (promotion.note.tone === 'ok' ? 'tw-text-v2-student-done' : 'tw-text-v2-error')
                            }
                        >
                            {promotion.note.tone === 'ok'
                                ? <LuCircleCheck size={14} aria-hidden className="tw-shrink-0" />
                                : <LuCircleAlert size={14} aria-hidden className="tw-shrink-0" />}
                            {promotion.note.text}
                        </p>
                    )}
                </div>

                <div className="tw-overflow-hidden tw-rounded-[12px] tw-border tw-border-solid tw-border-v2-neutral-200">
                    {isPriced && (
                        <div className="tw-flex tw-flex-col tw-gap-[10px] tw-px-[16px] tw-py-[14px]">
                            <Line label={t('v2.checkout.price')}>
                                <Money value={totals.price} />
                            </Line>

                            {totals.discount > 0 && (
                                <Line saving label={t('v2.checkout.discount')}>
                                    <Money minus value={totals.discount} />
                                </Line>
                            )}

                            {totals.code > 0 && (
                                <Line saving label={t('v2.checkout.codeDiscount')}>
                                    <Money minus value={totals.code} />
                                </Line>
                            )}
                        </div>
                    )}

                    <div
                        className={
                            'tw-flex tw-items-center tw-justify-between tw-gap-[16px] tw-bg-v2-neutral-50 tw-px-[16px] tw-py-[14px] ' +
                            (isPriced ? 'tw-border-0 tw-border-t tw-border-solid tw-border-v2-neutral-200' : '')
                        }
                    >
                        <span className="tw-text-[15px] tw-font-bold tw-leading-[1.6] tw-text-v2-neutral-900">
                            {t('v2.checkout.total')}
                        </span>
                        <span className="tw-text-[18px] tw-font-bold tw-leading-[1.5] tw-text-v2-brand">
                            {totals.total > 0 ? <Money value={totals.total} /> : t('v2.checkout.free')}
                        </span>
                    </div>
                </div>

                {needsPolicy && (
                    <div className="tw-flex tw-flex-col tw-gap-[10px]">
                        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-x-[12px] tw-gap-y-[8px]">
                            <label className="tw-m-0 tw-flex tw-min-w-0 tw-cursor-pointer tw-items-center tw-gap-[10px] tw-text-start tw-text-[14px] tw-leading-[1.6] tw-text-v2-neutral-900">
                                <Tick
                                    checked={agreed}
                                    disabled={submitting}
                                    onChange={(event) => setAgreed(event.target.checked)}
                                />
                                {t('v2.checkout.agree')}
                            </label>

                            <button
                                type="button"
                                aria-expanded={policyOpen}
                                aria-controls={POLICY_ID}
                                onClick={() => setPolicyOpen((open) => !open)}
                                className={`tw-inline-flex tw-shrink-0 tw-items-center tw-gap-[4px] tw-rounded-[6px] tw-text-[13px] tw-leading-[1.6] tw-text-v2-student-link ${FOCUS_RING}`}
                            >
                                <span className="hover:tw-underline">
                                    {policyOpen ? t('v2.checkout.hidePolicy') : t('v2.checkout.showPolicy')}
                                </span>
                                <LuChevronDown
                                    size={14}
                                    aria-hidden
                                    className={`tw-transition-transform ${policyOpen ? 'tw-rotate-180' : ''}`}
                                />
                            </button>
                        </div>

                        {policyOpen && (
                            <div
                                id={POLICY_ID}
                                aria-busy={policy.loading}
                                className="v2-prose tw-max-h-[220px] tw-overflow-y-auto tw-rounded-[12px] tw-border tw-border-solid tw-border-v2-neutral-200 tw-bg-v2-neutral-50 tw-p-[16px] tw-text-start"
                            >
                                {policyBody}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Modal>
    );
};

/**
 * Open while it has a package, and mounted only then — so each opening starts
 * from an empty form rather than the last one's code and ticked policy.
 */
const CheckoutDialogue = ({ pkg, onClose, onDone, onSubscriptions }) => (
    pkg
        ? <Checkout key={packageIdOf(pkg)} pkg={pkg} onClose={onClose} onDone={onDone} onSubscriptions={onSubscriptions} />
        : null
);

export default CheckoutDialogue;
